import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../lib/api';
import { useTenantFormat } from '../../hooks/useTenantFormat';
import { useTenant } from '../../contexts/TenantContext';
import { useToast } from '../../hooks/useToast';
import { GlassCard } from '../../components/GlassCard';
import BrandLogo from '../../components/BrandLogo';
import { Printer, Download, Receipt } from 'lucide-react';
import { arrayToCsvBlob, downloadBlob } from '../../lib/csv';

export default function TaxReport() {
  const { token, tenant } = useAuth();
  const { region } = useTenant();
  const toast = useToast();
  const { formatMoney, taxLabel, currencySymbol } = useTenantFormat();
  const cc = region?.country_code || 'IN';
  const isGST = cc === 'IN';
  const reportLabel = isGST ? `${taxLabel} Report` : `${taxLabel} Report`;

  const [month, setMonth] = useState(() => {
    const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    const fn = isGST ? api.erpGST : api.erpVAT;
    fn(token, { month }).then(setData).catch(e => { if (e.status !== 401) toast.show(e.message, 'error'); }).finally(() => setLoading(false));
  }, [token, month, isGST, toast]);

  const exportCsv = () => {
    if (!data) return;
    const rows = [
      { label: 'Total Sales', value: data.total_sales_rupees || data.total_sales || 0 },
      { label: `Output ${taxLabel}`, value: data.output_gst_rupees || data.output_vat || 0 },
      { label: 'Total Purchases', value: data.total_purchases_rupees || data.total_purchases || 0 },
      { label: `Input ${taxLabel} Credit`, value: data.input_gst_credit_rupees || data.input_vat_credit || 0 },
      { label: `Net ${taxLabel} Payable`, value: data.net_gst_payable_rupees || data.net_vat_payable || 0 },
    ];
    downloadBlob(arrayToCsvBlob(rows, ['label', 'value'], ['Line Item', `Amount (${currencySymbol})`]), `${taxLabel.toLowerCase()}-${month}.csv`);
    toast.show('CSV downloaded', 'success');
  };

  const rows = data ? [
    { label: 'Total Sales', value: data.total_sales_rupees ?? data.total_sales ?? 0, color: '#10B981' },
    { label: `Output ${taxLabel} Collected`, value: data.output_gst_rupees ?? data.output_vat ?? 0, color: '#F59E0B' },
    { label: 'Total Purchases', value: data.total_purchases_rupees ?? data.total_purchases ?? 0, color: '#3B82F6' },
    { label: `Input ${taxLabel} Credit`, value: data.input_gst_credit_rupees ?? data.input_vat_credit ?? 0, color: '#8B5CF6' },
    { label: `Net ${taxLabel} Payable`, value: data.net_gst_payable_rupees ?? data.net_vat_payable ?? 0, color: (data.net_gst_payable_rupees ?? data.net_vat_payable ?? 0) >= 0 ? '#EF4444' : '#10B981' },
  ] : [];

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto" data-testid="report-tax-page">
      <div className="hidden print:block mb-6">
        <div className="flex items-center gap-3 mb-2">
          <BrandLogo size={32} theme="light" />
          <div>
            <h1 className="text-xl font-extrabold text-gray-900">{tenant?.name || '1000AD'}</h1>
            <p className="text-gray-500 text-xs">{reportLabel} · {month} · Generated {new Date().toLocaleDateString('en-IN')}</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between mb-5 print:hidden">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-purple-50 border border-purple-200 flex items-center justify-center">
            <Receipt size={20} className="text-brand-purple" />
          </div>
          <div>
            <h1 className="text-gray-900 font-extrabold text-xl">{reportLabel}</h1>
            <p className="text-gray-500 text-xs">{data?.from_date && data?.to_date ? `${new Date(data.from_date).toLocaleDateString('en-IN')} – ${new Date(data.to_date).toLocaleDateString('en-IN')}` : month}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <input type="month" value={month} onChange={e => setMonth(e.target.value)} data-testid="tax-month-picker" className="h-10 px-3 rounded-xl border border-gray-200 bg-white text-gray-900 text-sm" />
          <button onClick={exportCsv} disabled={!data} data-testid="tax-export-csv" className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-gray-100 text-gray-700 text-sm font-semibold hover:bg-gray-200 disabled:opacity-40"><Download size={14} /> CSV</button>
          <button onClick={() => window.print()} data-testid="tax-print" className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-brand-purple text-white text-sm font-semibold hover:opacity-90"><Printer size={14} /> Print</button>
        </div>
      </div>

      {loading ? (
        <div className="py-12 flex justify-center"><div className="w-6 h-6 border-2 border-brand-purple border-t-transparent rounded-full animate-spin" /></div>
      ) : data ? (
        <div className="space-y-5">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            {rows.map(r => (
              <GlassCard key={r.label} className="p-4">
                <p className="text-gray-500 text-[10px] font-bold uppercase tracking-wider">{r.label}</p>
                <p className="font-extrabold text-lg tabular-nums mt-1" style={{ color: r.color }}>{formatMoney(r.value)}</p>
              </GlassCard>
            ))}
          </div>
          <GlassCard className="overflow-hidden" data-testid="tax-detail-card">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-gray-200 bg-gray-50">
                  <th className="text-left px-5 py-3 text-gray-500 text-xs font-bold uppercase">Line Item</th>
                  <th className="text-right px-5 py-3 text-gray-500 text-xs font-bold uppercase">Amount</th>
                </tr></thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} className={`border-b border-gray-100 ${i === rows.length - 1 ? 'bg-gray-50 font-extrabold' : ''}`}>
                      <td className="px-5 py-3 text-gray-900">{r.label}</td>
                      <td className="px-5 py-3 text-right tabular-nums" style={{ color: r.color }}>{formatMoney(r.value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </GlassCard>
          {data.invoice_count != null && (
            <p className="text-gray-500 text-xs">Based on {data.invoice_count} invoice{data.invoice_count !== 1 ? 's' : ''} and {data.purchase_count ?? 0} purchase{(data.purchase_count ?? 0) !== 1 ? 's' : ''} in period.</p>
          )}
        </div>
      ) : (
        <GlassCard className="p-12 text-center"><p className="text-gray-500">No tax data for this period.</p></GlassCard>
      )}
    </div>
  );
}
